import {
  useEntityDetailInstance,
  useEntityDetailMetadata,
  type CustomSectionSlot,
} from '@locus-taxy/entity-detail';
import { useClientId } from '../hooks/useClientId';
import { useEntityMetadata } from '../api/endpoints/useEntityMetadata';
import { useAppRegistry } from '../api/endpoints/useAppRegistry';
import { isMfeAppConfig } from '../utils/customApp';
import LoadingSpinner from '../components/LoadingSpinner';
import { CustomSectionRenderer } from './CustomSectionRenderer';

/**
 * The app-wide peek panel's `custom` detail-tab section, rendered through the
 * same {@link CustomSectionRenderer} the full details page mounts.
 *
 * The page builds its slot from context it already holds — the record it is
 * showing. The peek host is mounted once for the whole app and is handed only
 * the PEEKED record's `entityName` + `instanceId`, which is a different record
 * from whatever page is underneath. So every other input is resolved HERE,
 * against the peeked record: its instance (data + `rootInstanceKey`), its
 * entity metadata (display title, presentation config), its timeline
 * visibility, and the client's `apps` registry. Nothing is inherited from the
 * page — inheriting it would render this section against the wrong record.
 *
 * Both framework reads share the peek panel's own query keys, so neither opens a
 * new cache entry; `useEntityMetadata` is the single extra read, and it is the
 * read the peeked record's own details page would make. Sharing the key is not
 * the same as issuing nothing, though — see the `staleTime` below.
 *
 * `usecase` is deliberately NOT inherited from the route. The peek host blocks
 * the ambient usecase on purpose (`PeekBody` passes `request.usecase ?? ''`),
 * so a peeked record is drawn with its own default document rather than the
 * page's. This read has to agree with that, or the section resolves a different
 * view-config document from the tabs it is being rendered inside — wrong
 * `showTimeline`, a `usecase` handed to the MFE that the peek never selected,
 * and an error toast on every tab open when the peeked entity has no such
 * usecase.
 *
 * KNOWN LIMITATION: a peek opened WITH an explicit `PeekRequest.usecase` would
 * be honoured by `PeekBody` and not by this section, because `CustomSectionSlot`
 * is handed only `{section, entityName, instanceId}` and cannot see the request.
 * Dormant today — no opener sets it (both of encore-grid's `open({...})` calls
 * and entity-detail's `EntityRefWidget` omit it, checked in the shipped
 * bundles) — and closing it properly means threading `request.usecase` into the
 * slot's arguments, which is an `entity-detail` API change.
 */
/**
 * This section is a SECONDARY observer of an instance the peek panel has already
 * fetched, so it must not evaluate staleness at the default `0`.
 *
 * `staleTime` is evaluated per OBSERVER against the shared cache entry, so a `0`
 * observer mounting moments after the primary fetch completed triggers its own
 * redundant refetch of the identical query — which is what this section did on
 * every custom-tab visit, five tab toggles giving five extra instance reads.
 * `useEntityDetailInstance`'s own JSDoc states the requirement, and the two
 * other secondary observers in the framework (the tab-count probe and the
 * related-grid resolver already wired into this same host) both pass it.
 *
 * Same 30s the framework uses. It is not exported, so this names it rather than
 * reaching into the package internals for it.
 */
const SECONDARY_INSTANCE_OBSERVER_STALE_TIME_MS = 30_000;

function PeekCustomSection({
  section,
  entityName,
  instanceId,
}: Readonly<Parameters<CustomSectionSlot>[0]>) {
  const clientId = useClientId();
  const { entityData = {}, rootInstanceKey } = useEntityDetailInstance(
    entityName,
    instanceId,
    { staleTime: SECONDARY_INSTANCE_OBSERVER_STALE_TIME_MS }
  );
  /*
   * THE EMPTY STRING IS LOAD-BEARING, and it is the framework's own convention
   * for an embedded read: `useEntityDetailMetadata` resolves
   * `usecaseOverride ?? contextUsecase`, so `''` explicitly BLOCKS the ambient
   * route usecase where `undefined` would inherit it. `PeekBody` passes
   * `request.usecase ?? ''` for exactly this reason — a peeked record is not the
   * page's record, and the page's view-config document is the wrong one to draw
   * it with. Passing `''` here also shares PeekBody's cache key instead of
   * forking a second one.
   */
  const { timelineVisible } = useEntityDetailMetadata(entityName, '');
  const { data: entityMetadata } = useEntityMetadata(clientId, entityName, {
    usecase: undefined,
  });
  const { remoteAppFor, isReady: registryReady } = useAppRegistry(clientId);

  const entitySchemaInfo = entityMetadata?.data?.entitySchema as
    | { title?: string }
    | undefined;

  // Same two gates the page applies before mounting a section app: a name that
  // is not a valid MFE id resolves to nothing, and the registry lookup has to
  // land before `remoteAppFor` can answer.
  if (!isMfeAppConfig({ name: section.name, appName: section.name })) {
    return null;
  }
  if (!registryReady) {
    return <LoadingSpinner />;
  }

  return (
    <CustomSectionRenderer
      section={section}
      entityName={entityName}
      instanceId={instanceId}
      clientId={clientId}
      entityTitle={entitySchemaInfo?.title ?? ''}
      entityData={entityData}
      rootInstanceKey={rootInstanceKey}
      showTimeline={timelineVisible}
      entitySchemaInfo={entitySchemaInfo}
      usecase={undefined}
      remoteAppFor={remoteAppFor}
    />
  );
}

/**
 * Slot handed to the app-wide `EntityPeekHost`. `SectionDispatcher` calls it
 * directly, so the hooks above must sit one level down in an ELEMENT — calling
 * them inline would run them inside the dispatcher's own render and reorder its
 * hooks as the peeked record's sections change.
 */
export const PeekCustomSectionSlot: CustomSectionSlot = props => (
  <PeekCustomSection {...props} />
);
